/* eslint-disable no-console */
import React, { useEffect, useReducer } from 'react';
import classnames from 'classnames';
import convert from 'htmr';
import { flow, isEmpty, map, replace, set } from 'lodash/fp';
import IconCaret from '../../files/caret.svg';

const mapWithIndex = map.convert({ cap: false });

function MainNavAccordion({
  level,
  url,
  current,
  label,
  children,
  active,
  index,
  dispatch,
}) {
  const toggleActive = (evt) => {
    evt.preventDefault();
    dispatch({ type: `setActiveLevel${level}`, index: active ? -1 : index });
  };
  const setActive = (evt) => {
    evt.preventDefault();
    dispatch({ type: `setActiveLevel${level}`, index });
  };

  const getPreviousLevelUrl = (url) => {
    const splitUrl = url.split('/');
    splitUrl.pop();
    return splitUrl.join('/');
  };

  const itemClasses = classnames(`main-nav__item main-nav__item--l${level}`, {
    current,
    active,
  });
  const linkClasses = classnames(`main-nav__link main-nav__link--l${level}`, {
    current,
    active,
  });
  const buttonClasses = classnames(`main-nav__btn main-nav__btn--l${level}`, {
    current,
    active,
  });
  const listClasses = classnames(
    `main-nav__list main-nav__list--l${level + 1} margins-off`,
    { current, active }
  );
  const listId = `main-nav-${replace(/\W+/g, '-', url)}`;

  if (level === 3 && index > 5) {
    return null;
  }

  if (level === 3 && index === 5) {
    return (
      <li key={url}>
        <div className={itemClasses} onMouseEnter={setActive}>
          <a href={getPreviousLevelUrl(url)} className={linkClasses}>
            More...
          </a>
        </div>
      </li>
    );
  }

  return (
    <li key={url}>
      <div className={itemClasses} onMouseEnter={setActive}>
        <a href={url} className={linkClasses}>
          {convert(label)}
        </a>
        {!isEmpty(children) && (
          <button
            className={buttonClasses}
            onClick={toggleActive}
            onTouchEnd={toggleActive}
            aria-expanded={active}
            aria-label={`${
              active ? 'Collapse' : 'Expand'
            } sub-menu for ${label}`}
            aria-controls={listId}
          >
            <IconCaret focusable={false} width="16" height="9" />
          </button>
        )}
      </div>
      {!isEmpty(children) && (
        <ul id={listId} className={listClasses}>
          {mapWithIndex(
            (item, index) => MainNavAccordion({ ...item, index, dispatch }),
            children
          )}
        </ul>
      )}
    </li>
  );
}

const MainNav = ({ mainNav, id }) => {
  if (isEmpty(mainNav)) {
    return null;
  }

  const initialState = {
    levelOneActiveIndex: -1,
    levelTwoActiveIndex: -1,
    mainNav,
  };

  function mainNavReducer(state, action) {
    switch (action.type) {
      case 'setActiveLevel1':
        return {
          levelOneActiveIndex: action.index,
          levelTwoActiveIndex: -1,
          mainNav: flow(
            map(set('active', false)),
            set([action.index, 'active'], true)
          )(state.mainNav),
        };
      case 'setActiveLevel2':
        return {
          levelOneActiveIndex: state.levelOneActiveIndex,
          levelTwoActiveIndex: action.index,
          mainNav: mapWithIndex(
            (itemL1, idxL1) =>
              idxL1 === state.levelOneActiveIndex
                ? set(
                    'children',
                    mapWithIndex((itemL2, idxL2) =>
                      set('active', idxL2 === action.index, itemL2)
                    )(itemL1.children),
                    itemL1
                  )
                : itemL1,
            state.mainNav
          ),
        };
      case 'resetActive':
        return initialState;
      default:
        return state;
    }
  }

  const [state, dispatch] = useReducer(mainNavReducer, initialState);

  const resetActive = () => dispatch({ type: 'resetActive' });

  const handleKeyDown = (evt) => {
    if (
      evt.key === 'Escape' ||
      evt.target.classList.contains('header__btn--search')
    ) {
      resetActive();
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    // Remove event listener on cleanup
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []); // Empty array ensures that effect is only run on mount and unmount

  return (
    <nav className="main-nav" onMouseLeave={resetActive} id={id}>
      <ul className="main-nav__list main-nav__list--l1 margins-off">
        {mapWithIndex(
          (item, index) => MainNavAccordion({ ...item, index, dispatch }),
          state.mainNav
        )}
      </ul>
    </nav>
  );
};

export default MainNav;
